import { createStyles, Loader } from '@mantine/core';
import Link from 'next/link';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import React, { forwardRef } from 'react';

import { textStyles } from '~/domains/design-system/Text';

export type ButtonProps = {
  variant?:
    | 'primary-fill'
    | 'primary-stroke'
    | 'secondary-stroke'
    | 'secondary-fill'
    | 'secondary-inverted-stroke'
    | 'secondary-inverted-fill'
    | 'tertiary-fill'
    | 'tertiary-inverted-fill'
    | 'danger-fill'
    | 'warning-fill'
    | 'info-fill'
    | 'text'
    | 'primary-text';
  size?:
    | 'small'
    | 'medium'
    | 'large'
    | 'x-small-icon'
    | 'small-icon'
    | 'small-icon-text'
    | 'medium-icon'
    | 'large-icon';
  mode?: 'default' | 'linkButton' | 'unstyledButton' | 'inlineLink';
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  noWrap?: boolean;
};

const useStyles = createStyles((theme) => ({
  base: {
    border: 0,
    borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ '4px',
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '0px 18px',
    cursor: 'pointer',
    fontFamily: theme.fontFamily,

    'a&': {
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    '&:focus-visible': {
      outlineOffset: '2px',
      outline: `2px solid ${theme.colors.splash[5]}`,
    },

    '&:disabled': {
      cursor: 'not-allowed',
    },

    '@media (hover: hover)': {
      transition: 'opacity 150ms ease-in-out',
    },
  },
  'primary-fill': {
    color: theme.colors.gray2[7],
    background: theme.colors.blue[4],

    '&:disabled': {
      color: theme.colors.gray2[4],
      background: theme.colors.blue[1],
    },

    '&::not(:disabled):hover': {
      background: theme.other.color.blue.hover,
    },
  },
  'primary-stroke': {
    color: theme.colors.blue[4],
    background: 'transparent',
    border: `2px solid ${theme.colors.blue[4]}`,

    '&:disabled': {
      color: theme.colors.blue[1],
      borderColor: theme.colors.blue[1],
    },
  },
  'secondary-stroke': {
    background: 'transparent',
    color: theme.colors.gray2[7],
    border: `2px solid ${theme.colors.gray2[7]}`,

    '&:disabled': {
      color: theme.colors.gray2[3],
      borderColor: theme.colors.gray2[3],
    },
  },
  'secondary-fill': {
    color: theme.white,
    background: theme.colors.gray2[7],

    '&:disabled': {
      color: theme.colors.gray2[1],
      background: theme.colors.gray2[3],
    },
  },
  'secondary-inverted-fill': {
    color: theme.colors.gray2[7],
    background: theme.white,

    '&:disabled': {
      background: theme.colors.gray2[1],
      color: theme.colors.gray2[3],
    },
  },
  'secondary-inverted-stroke': {
    background: 'transparent',
    color: theme.white,
    border: `2px solid ${theme.white}`,

    '&:disabled': {
      color: theme.colors.gray2[3],
      borderColor: theme.colors.gray2[3],
    },
  },
  'tertiary-fill': {
    color: theme.colors.gray2[6],
    background: theme.colors.gray2[0],

    '&:disabled': {
      color: theme.colors.gray2[2],
      background: theme.colors.gray2[0],
    },
  },
  'tertiary-inverted-fill': {
    color: theme.colors.gray2[0],
    background: theme.colors.gray2[6],

    '&:disabled': {
      color: theme.colors.gray2[0],
      background: theme.colors.gray2[2],
    },
  },
  'danger-fill': {
    color: theme.white,
    background: theme.colors.red[3],

    '&:disabled': {
      color: theme.colors.gray2[1],
      background: theme.colors.red[1],
    },
  },
  'warning-fill': {
    color: theme.colors.gray2[7],
    background: theme.colors.yellow[4],

    '&:disabled': {
      color: theme.colors.gray2[7],
      background: theme.colors.yellow[1],
    },
  },
  'info-fill': {
    color: theme.white,
    background: theme.colors.indigo[9],

    '&:disabled': {
      color: theme.colors.gray2[1],
      background: theme.colors.indigo[4],
    },
  },
  text: {
    background: 'transparent',
    color: theme.colors.gray[9],

    '&:disabled': {
      color: theme.colors.gray[6],
    },
  },
  'primary-text': {
    background: 'transparent',
    color: theme.colors.blue[7],

    '&:hover:not(:disabled), &:focus-visible': {
      textDecoration: 'underline',
    },

    '&:disabled': {
      color: theme.colors.gray[6],
    },
  },
  small: {
    ...textStyles['label-small'],
    height: '32px',
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '0px 15px',
  },
  'x-small-icon': {
    height: '22px',
    width: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  'small-icon': {
    height: '32px',
    width: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  'small-icon-text': {
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '0 8px',

    'svg:first-of-type': {
      marginRight: /* eslint-disable-line local-rules/use-theme-tokens */ '4px',
    },
    'svg:last-of-type': {
      marginLeft: /* eslint-disable-line local-rules/use-theme-tokens */ '4px',
    },
  },
  medium: { ...textStyles['label-medium'], height: '40px' },
  'medium-icon': {
    height: '40px',
    width: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  large: { ...textStyles['label-large'], height: '48px' },
  'large-icon': {
    ...textStyles['label-large'],
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    'svg:first-of-type': {
      marginRight: /* eslint-disable-line local-rules/use-theme-tokens */ '8px',
    },
    'svg:last-of-type': {
      marginLeft: /* eslint-disable-line local-rules/use-theme-tokens */ '8px',
    },
  },
  fullWidth: { width: '100%' },
  linkButton: {
    padding: 0,
    height: 'auto',
    color: theme.colors.blue[7],
    background: 'transparent',
    textDecoration: 'none',
    border: 'none',
    '&:hover': {
      textDecoration: 'underline',
      background: 'none',
    },
    '&:disabled': {
      color: theme.colors.blue[7],
      background: 'transparent',
      cursor: 'default',
      opacity: 0.5,
    },
  },
  unstyledButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
  },
  inlineLink: {
    display: 'inline !important',
    height: 'auto',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    fontWeight: 700,
    color: theme.colors.blue[7],

    '&:hover': {
      textDecoration: 'underline',
    },
  },
  loading: {
    position: 'relative',
    color: 'transparent !important',

    '& .loader': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
}));

const ButtonFactory = <E extends React.ElementType>(
  component: E,
  defaultMode: ButtonProps['mode'] = 'default'
) =>
  // eslint-disable-next-line react/display-name
  forwardRef<React.ElementRef<E>, ButtonProps & React.ComponentPropsWithoutRef<E>>(
    (
      {
        children,
        className,
        variant = 'primary-fill',
        size = 'large',
        mode = defaultMode,
        fullWidth,
        loading,
        noWrap,
        disabled,
        ...props
      },
      ref
    ) => {
      const { classes, cx } = useStyles();
      return React.createElement(
        component,
        {
          ref,
          className: cx(
            classes.base,
            classes[variant],
            classes[size],
            classes[mode],
            fullWidth && classes.fullWidth,
            loading && classes.loading,
            noWrap && classes.noWrap,
            className
          ),
          disabled: loading || disabled, // when button is loading, it should be disabled to prevent double/rage clicks
          ...props,
        },
        loading ? (
          <>
            {children}
            <Loader size="sm" className="loader" />
          </>
        ) : (
          children
        )
      );
    }
  );

export const Button = ButtonFactory('button');
Button.displayName = 'Button';

export const ButtonLink = ButtonFactory(Link);
ButtonLink.displayName = 'ButtonLink';

export const LinkButton = ButtonFactory('button', 'linkButton');
LinkButton.displayName = 'LinkButton';

export const ButtonLabel = ButtonFactory('label');
Button.displayName = 'ButtonLabel';

export const UnstyledButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { classes, cx } = useStyles();
  return <button className={cx(classes.unstyledButton, className)} {...props} ref={ref} />;
});

UnstyledButton.displayName = 'UnstyledButton';

export const UnstyledButtonLink = forwardRef<
  HTMLAnchorElement,
  AnchorHTMLAttributes<HTMLAnchorElement>
>(({ className, href, ...props }, ref) => {
  const { classes, cx } = useStyles();
  return (
    <Link href={href} className={cx(classes.unstyledButton, className)} {...props} ref={ref} />
  );
});

UnstyledButtonLink.displayName = 'UnstyledButtonLink';
